import { Box, BoxProps, styled } from "@mui/material";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { Fragment, useState } from "react";
import {
  CarInfoCell,
  CarInfoHeader,
  CarInfoTable,
} from "@app/components/InfoTable";
import { formatNumber, formatPercentFactor, isOddEven } from "@app/utils";
import {
  SimulationBehaviorEquity,
  useAssetClassesSimulationBehaviorEquityListQuery,
  useAssetClassesSimulationBehaviorEquityUpdateMutation,
} from "@app/api/carApi.generated";
import { CarNumberFieldDelayed } from "@app/components/NumberField";
import { CarDateField } from "@app/components/DateField";
import { CarFlatPageControl } from "@app/components/FlatPageControl";
import { CarTextFieldDelayed } from "@app/components/Inputs";
import { CarKatexCalcField } from "@app/components/Katex";

const StyledBox = styled(Box)(({ theme }) => ({
  border: "solid 1px",
  borderColor: theme.palette.gray6,
  borderRadius: "5px",
  backgroundColor: theme.palette.white,
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(5.5),
}));

enum BoundaryTypes {
  TYPE_UPPER = 0,
  TYPE_LOWER = 1,
  TYPE_EXPECTED_RETURN = 2,
}

interface BoundaryItem {
  year: number;
  label: string;
  expectedReturn?: number;
  upperValue?: number;
  lowerValue?: number;
}

const BoundaryTable = (props: { items: BoundaryItem[] }) => {
  return (
    <StyledBox>
      <CarInfoTable sx={{ gridTemplateColumns: "0.5fr 1fr 1fr 1fr" }}>
        <CarInfoHeader text="" />
        <CarInfoHeader text="Expected Return" />
        <CarInfoHeader text="Lower Boundary" />
        <CarInfoHeader text="Upper Boundary" />
        {props.items.map((row, rowIdx) => (
          <Fragment key={rowIdx}>
            <CarInfoCell
              isOdd={isOddEven(rowIdx)}
              justifyContent="start"
              text={row.year.toString()}
            />
            <CarInfoCell
              isOdd={isOddEven(rowIdx)}
              text={
                row.expectedReturn
                  ? formatPercentFactor(row.expectedReturn, {
                      decimalPlaces: 4,
                    })
                  : "-"
              }
            />
            <CarInfoCell
              isOdd={isOddEven(rowIdx)}
              text={
                row.lowerValue
                  ? formatNumber(row.lowerValue, { decimalPlaces: 6 })
                  : "-"
              }
            />
            <CarInfoCell
              isOdd={isOddEven(rowIdx)}
              text={
                row.upperValue
                  ? formatNumber(row.upperValue, { decimalPlaces: 6 })
                  : "-"
              }
            />
          </Fragment>
        ))}
      </CarInfoTable>
    </StyledBox>
  );
};

interface SimulationModelsBehaviorEquityProps {
  assetClassId: string;
  sx?: BoxProps["sx"];
}

export const SimulationModelsBehaviorEquity = (
  props: SimulationModelsBehaviorEquityProps,
) => {
  const [currentTab, setCurrentTab] = useState(0);

  const data = useAssetClassesSimulationBehaviorEquityListQuery({
    id: "",
  });

  const [updateMut] = useAssetClassesSimulationBehaviorEquityUpdateMutation();

  const item = data.data?.find((be) =>
    be.assetclasslevel4_set?.includes(props.assetClassId),
  );

  const updateItem = (value: SimulationBehaviorEquity) => {
    updateMut({
      id: value.id ?? "",
      simulationBehaviorEquity: value,
    });
  };

  const getBoundaryItems = (isManual: boolean): BoundaryItem[] => {
    const boundaries =
      item?.boundary_group?.boundaries?.filter((i) => i.manual === isManual) ??
      [];

    const years = Array.from(new Set(boundaries.map((i) => i.year))).sort(
      (a, b) => a - b,
    );

    return years.map<BoundaryItem>((year) => ({
      year,
      expectedReturn: boundaries.find(
        (i) =>
          i.boundary_type === BoundaryTypes.TYPE_EXPECTED_RETURN &&
          i.year === year,
      )?.value,
      label: isManual ? "Manual" : "System Generated",
      lowerValue: boundaries.find(
        (i) => i.boundary_type === BoundaryTypes.TYPE_LOWER && i.year === year,
      )?.value,
      upperValue: boundaries.find(
        (i) => i.boundary_type === BoundaryTypes.TYPE_UPPER && i.year === year,
      )?.value,
    }));
  };

  const manualItems = getBoundaryItems(true);
  const systemItems = getBoundaryItems(false);

  return (
    <Box sx={{ ...props.sx, display: "flex", flexDirection: "column" }}>
      <TablePlaceHolder isLoading={data.isLoading} rows={8}>
        {item && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "180px 180px",
              columnGap: 11,
              rowGap: 5,
              ".MuiInputBase-root": {
                backgroundColor: "white",
              },
            }}
          >
            <CarKatexCalcField
              label="Trend Equation"
              value={item.trend_equation_katex}
            />
            <div />
            <CarDateField
              sx={{
                ".MuiOutlinedInput-root": { backgroundColor: "white" },
                ".MuiButtonBase-root": {
                  marginRight: 0,
                },
              }}
              label="Trend Start Date"
              value={item.trend_start_date}
              onChange={(trend_start_date) =>
                updateItem({
                  ...item,
                  trend_start_date: trend_start_date ?? "",
                })
              }
            />

            <div />
            <CarKatexCalcField
              label="Model Equation"
              value={item.regression_result?.equation_katex}
            />
            <div />
            <CarNumberFieldDelayed
              label="Static Model Calculated Mu"
              decimalPlaces={10}
              value={item.static_model_calculated_mu}
              onChange={(static_model_calculated_mu) =>
                updateItem({
                  ...item,
                  static_model_calculated_mu: static_model_calculated_mu ?? 0,
                })
              }
            />
            <div />
            <CarNumberFieldDelayed
              label="Static Model Mu Override"
              decimalPlaces={10}
              value={item.static_model_mu_override}
              onChange={(static_model_mu_override) =>
                updateItem({
                  ...item,
                  static_model_mu_override: static_model_mu_override ?? 0,
                })
              }
            />
            <CarTextFieldDelayed
              label="Static Model Mu Override Rationale"
              sx={{ gridColumn: "1 / span 2" }}
              fullWidth
              multiline
              rows={5}
              value={item.static_model_mu_override_rationale}
              onChange={(static_model_mu_override_rationale) =>
                updateItem({
                  ...item,
                  static_model_mu_override_rationale,
                })
              }
            />
          </Box>
        )}
        {(manualItems.length || systemItems.length) && (
          <CarFlatPageControl
            sx={{
              mt: 5,
              ".flat-page-control-header": {
                ml: 3,
                gap: 3,
              },
              ".flat-page-control-button": {
                p: 1,
                fontSize: "18px",
              },
            }}
            items={[
              {
                label: "Boundary Group (Manual)",
                value: 0,
                content: <BoundaryTable items={manualItems} />,
              },
              {
                label: "Boundary Group (System Generated)",
                value: 1,
                content: <BoundaryTable items={systemItems} />,
              },
            ]}
            value={currentTab}
            onChange={setCurrentTab}
          />
        )}
      </TablePlaceHolder>
    </Box>
  );
};
